// @flow

import * as React from 'react';

import { useNavigate } from 'react-router-dom';

import type { StaticHandlerArgs } from '@gandi/app-runner/server';
import type {
  Currency,
  CreatePrepaidAction,
  ReplacePrepaidAction,
} from '@gandi/billing';
import type {
  Contract,
  FetchContractsParams,
  FetchContractsAction,
} from '@gandi/contracts';
import type { FetchResponse } from '@gandi/fetcher';
import type { NotifyAction } from '@gandi/notifications';
import type {
  OrganizationCreateData,
  PatchOneOrganizationAction,
  CreateOneOrganizationAction,
} from '@gandi/redux-organization';
import type { Organization } from '@gandi/redux-sharing';
import type { TranslatorProps } from '@gandi/translate';
import { Base, Button } from '@gandi/ui';

import type { GandiLogger } from 'gandi.redux-logger';

import {
  renderOption as renderOrgInfo,
  getDisplayName as getName,
} from 'src/Cart/AccountChoice/components/PayerSelect';
import { useQuery } from 'src/helpers/withRouter';

import { Layout } from '../../components/Layout';
import type {
  WithRouterProps,
  WithNeedsAuthProps,
  FullState,
} from '../../types';

import BecomeReseller from './components/BecomeReseller';
import Delegate from './components/Delegate';
import ResellerContracts from './components/ResellerContracts';
import ResellerDisclaimer from './components/ResellerDisclaimer';

type Params = { cartId: string, id: 'new' | string };
type Query = {
  // TO CONFIRM
  owner?: 'cart' | 'products',
};

export type InjectedProps = {
  ...WithRouterProps<Params>,
  ...WithNeedsAuthProps,
  translator: TranslatorProps,
  logger: GandiLogger,
};

export type StateProps = {
  personalId: string,
  org: Organization,
  orgFormValues: {
    ...OrganizationCreateData | Partial<OrganizationCreateData>,
    currency?: Currency,
  },
  currency: ?Currency,
  contractParams: FetchContractsParams,
  loadedContracts: boolean,
  fetchingContracts: boolean,
  failureContracts: boolean,
  contracts: Array<Contract>,
};

export type DispatchProps = {
  fetchContracts: FetchContractsAction<Promise<any>>,
  patchOrg: PatchOneOrganizationAction,
  createOrg: CreateOneOrganizationAction,
  createPrepaid: CreatePrepaidAction<Promise<any>>,
  replacePrepaid: ReplacePrepaidAction<Promise<any>>,
  setCurrentId: (id: string) => void,
  setCartProductOwner: (id: string) => void,
  fetchOneOrganization: (id: string) => Promise<any>,
  invalidateOrganizations: () => void,
  destroyForm: () => mixed,
  notifyDanger: NotifyAction,
};

export type Props = {
  ...InjectedProps,
  ...StateProps,
  ...DispatchProps,
};

function getOrgId(response: FetchResponse) {
  // FIXME ask to api to return id in response.data
  // $FlowFixMe[incompatible-use]
  return response.headers.get('Location').split('/').pop();
}

const addressFields = ['streetaddr', 'streetaddr2', 'city', 'state', 'zip'];

const baseFields = [...addressFields, 'country', 'email', 'phone', 'fax'];

const cleanEmptyValue = (value: string): string | null =>
  value === '' ? null : value;

const identityFields = ['firstname', 'lastname'];

function filterValues(
  personal: boolean,
  values: StateProps['orgFormValues'],
  org: Organization,
) {
  const fields = [...baseFields];

  identityFields.forEach(field => {
    if (!personal || !org[field]) {
      fields.push(field);
    }
  });

  if (!personal) {
    fields.push('siren', 'vat_number');
  }

  const countryChanged = values.country && org.country !== values.country;

  const keys = Object.keys(values);

  return keys.reduce((acc, current) => {
    const validField = fields.includes(current);
    // $FlowFixMe - differences between form and Organization types
    const updatedField = org && org[current] !== values[current];
    const addressField = countryChanged && addressFields.includes(current);

    if (validField && (!org || updatedField || addressField)) {
      // $FlowFixMe - differences between form and Organization types
      acc[current] = cleanEmptyValue(values[current]);
    }

    return acc;
  }, {});
}

type ContentProps = {
  translator: InjectedProps['translator'],
  back: string,
  loading: boolean,
  failure: boolean,
  disabled: boolean,
  onContinueClick: () => any,
  children: React.Node,
};

function Content({
  children,
  translator,
  back,
  loading,
  failure,
  disabled,
  onContinueClick,
}: ContentProps) {
  const { __ } = translator;

  const handleContinueClick = (evt: SyntheticMouseEvent<HTMLButtonElement>) => {
    evt.preventDefault();
    onContinueClick();
  };

  return (
    <Layout currentStep={1}>
      <Base marginBottom="base">{children}</Base>
      <Button
        kind="outline"
        to={loading ? undefined : back}
        disabled={loading}
        grouped
      >
        {__('Edit')}
      </Button>
      <Button
        onClick={handleContinueClick}
        disabled={loading || failure || disabled}
        loading={loading}
      >
        {__('Continue')}
      </Button>
    </Layout>
  );
}

function Presenter(props: Props): React.Node {
  const {
    params: { cartId, id },
    translator,
    translator: { __ },
    personalId,
    org,
    orgFormValues,
    currency,
    contracts,
    contractParams,
    loadedContracts,
    fetchingContracts,
    failureContracts,
    fetchContracts,
    patchOrg,
    createOrg,
    createPrepaid,
    replacePrepaid,
    setCurrentId,
    setCartProductOwner,
    fetchOneOrganization,
    invalidateOrganizations,
    destroyForm,
    notifyDanger,
    logger,
  } = props;
  const navigate = useNavigate();
  const { owner = 'cart' } = useQuery<Query>();

  const update = id !== 'new';
  const personal = update && id === personalId;

  const back = `/cart/${cartId}/configure/account/${
    update ? 'organization' : 'new'
  }?owner=${owner}`;
  const next = `/cart/${cartId}/configure/account-choice`;

  const sharingId = update ? id : undefined;
  const mayDelegate = !update && owner === 'products';

  const [loading, setLoading] = React.useState(false);
  const [delegate, setDelegate] = React.useState(false);
  const [asReseller, setAsReseller] = React.useState({
    checked: false,
    approved: 0,
  });

  const mayBecomeReseller =
    owner === 'cart' &&
    !update &&
    !delegate &&
    (!update || !org.reseller) &&
    (update ? org : orgFormValues).type !== 'individual';

  React.useEffect(() => {
    const shouldFetch =
      mayBecomeReseller &&
      !contracts.length &&
      !loadedContracts &&
      !fetchingContracts &&
      !failureContracts;

    if (shouldFetch) {
      fetchContracts(contractParams);
    }
  }, [
    mayBecomeReseller,
    loadedContracts,
    fetchingContracts,
    failureContracts,
    contracts,
    fetchContracts,
    contractParams,
  ]);

  const onContinueClick = async () => {
    setLoading(true);

    let newId = '';
    if (!update && !sharingId) {
      const createValues = delegate
        ? {
            ...orgFormValues,
            invitation: {
              workflow: 'buy4me',
              params: {
                billed_orgname: org.name,
                cart_id: cartId,
              },
            },
            team: { profile: 'admin', name: __('Admin') },
          }
        : orgFormValues;
      // $FlowFixMe
      newId = await createOrg(createValues, { dryrun: false }).then(getOrgId);
    } else if (update && sharingId) {
      const filteredValues = filterValues(personal, orgFormValues, org);
      await patchOrg(sharingId, filteredValues, { dryrun: false });
    }

    if (orgFormValues.currency) {
      if (!currency) {
        await createPrepaid(
          { currency: orgFormValues.currency, optimistic: true },
          { sharingId: sharingId || newId },
        );
      } else if (currency && orgFormValues.currency !== currency) {
        await replacePrepaid(
          { currency: orgFormValues.currency },
          { sharingId },
        );
      }
    }

    const { checked, approved } = asReseller;
    const validBecomeReseller =
      checked && contracts.length > 0 && approved === contracts.length;
    if (validBecomeReseller) {
      try {
        await patchOrg(
          sharingId || newId,
          { reseller: true },
          { dryrun: false },
        );
      } catch (err) {
        setLoading(false);
        notifyDanger(translator.__('Unexpected error occurred'));
        logger.error({ err }, 'Unable to set org as reseller.');
        return;
      }
    }

    invalidateOrganizations();

    // Always either case, creation or update, this makes flow ~ok
    const orgId = newId || sharingId || '';
    if (orgId) {
      await fetchOneOrganization(orgId);
    }

    if (newId) {
      if (owner === 'cart') setCurrentId(newId);
      if (owner === 'products') setCartProductOwner(newId);
    }
    navigate(next, { replace: true });
    destroyForm();

    // These other dependencies can't change.
  };

  const handleBecomeResellerChange = React.useCallback(
    (checked: boolean) => {
      setAsReseller({ checked, approved: 0 });
    },
    [setAsReseller],
  );

  const handleContractChange = React.useCallback(
    (checked: boolean) => {
      setAsReseller(previous => ({
        ...previous,
        // $FlowFixMe[unsafe-addition]: checked is a boolean :shrug:
        approved: previous.approved + checked,
      }));
    },
    [setAsReseller],
  );

  const noContracts =
    mayBecomeReseller &&
    asReseller.checked &&
    (failureContracts || (loadedContracts && !contracts.length));
  const invalidBecomeReseller =
    asReseller.checked &&
    (!asReseller.approved || asReseller.approved !== contracts.length);

  return (
    <Content
      translator={translator}
      loading={loading}
      failure={noContracts}
      disabled={invalidBecomeReseller}
      back={back}
      onContinueClick={onContinueClick}
    >
      <h1>{__('Verify your information')}</h1>
      <Base
        marginBottom={mayBecomeReseller || mayDelegate ? 'small' : undefined}
        backgroundColor="surface"
      >
        {renderOrgInfo(
          translator.__,
          '',
          false,
          // $FlowFixMe - Extract helper w/ looser type
          update ? { ...org, ...orgFormValues } : orgFormValues,
        )}
      </Base>
      {mayDelegate && (
        <Delegate translator={translator} onChange={setDelegate} />
      )}
      {mayBecomeReseller && (
        <>
          <BecomeReseller
            translator={translator}
            onChange={handleBecomeResellerChange}
          />
          {asReseller.checked && (
            <>
              <ResellerDisclaimer
                translator={translator}
                orgName={getName(
                  translator.__,
                  // $FlowFixMe - Extract helper w/ looser type
                  update ? { ...org, ...orgFormValues } : orgFormValues,
                )}
              />
              <ResellerContracts
                translator={translator}
                onChange={handleContractChange}
                contracts={contracts}
                failure={noContracts}
              />
            </>
          )}
        </>
      )}
    </Content>
  );
}

Presenter.handleRedirect = function handleRedirect(
  args: StaticHandlerArgs<{}, Params, empty, FullState>,
): Promise<any> {
  const cartId = args.params.cartId;

  const redirectTo = `/cart/${cartId}/configure/account-choice`;
  args.redirect(redirectTo);
  throw new Error('Redirecting to account-choice.');
};

export default Presenter;
